import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Languages, Delete, User, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import api from "@/api";
import GtLogo from "@/components/GtLogo";

// Premium, branding-only login screen for GT Royal Partner App.
// No marketing copy. Large logo. Visible black input text.
export default function Login() {
  const { login, phoneLogin, user } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [lookupErr, setLookupErr] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(!localStorage.getItem("xtra_lang_picked"));
  const [blockedNotice] = useState(localStorage.getItem("xtra_blocked") === "1");

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);
  useEffect(() => {
    if (blockedNotice) {
      toast.error(t("blocked_message"));
      localStorage.removeItem("xtra_blocked");
    }
  // eslint-disable-next-line
  }, []);

  const lookupAndAdvance = async () => {
    if (phone.length !== 10) return;
    setBusy(true); setLookupErr(null);
    try {
      const r = await api.get(`/auth/lookup?phone=${phone}`);
      // Apr 2026: Retailer/Wholesaler bypass PIN — straight phone login.
      if (r.data.role === "retailer" || r.data.role === "wholesaler") {
        try {
          const u = await phoneLogin(phone);
          localStorage.setItem("xtra_lang_picked", "1");
          toast.success(`${t("welcome")}, ${u.name}`);
          // Navigation handled by the useEffect above (user state change).
        } catch (le) {
          const s2 = le?.response?.status;
          const d2 = le?.response?.data?.detail;
          if (s2 === 404 || d2 === "NOT_REGISTERED") setLookupErr(t("no_shop_found"));
          else if (d2 === "ACCOUNT_BLOCKED") setLookupErr(lang === "ta" ? "கணக்கு தடை" : "Account blocked");
          else setLookupErr(lang === "ta" ? "பிழை" : "Error");
        }
        return;
      }
      // Staff roles continue with the PIN pad.
      setName(r.data.name); setStep("pin");
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) setLookupErr(t("no_shop_found"));
      else if (s === 429) setLookupErr(lang === "ta" ? "அதிகமா முயற்சிச்சீங்க. கொஞ்சம் பிறகு முயற்சி செய்" : "Too many attempts. Try again later.");
      else setLookupErr(lang === "ta" ? "பிழை" : "Error");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (pin.length !== 4) return;
    setBusy(true);
    try {
      const u = await login(phone, pin);
      localStorage.setItem("xtra_lang_picked", "1");
      toast.success(`${t("welcome")}, ${u.name}`);
      // Apr 2026: Manual attendance — no silent auto-check-in toast. User must tap Check In on dashboard.
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (detail === "ACCOUNT_BLOCKED") toast.error(lang === "ta" ? "கணக்கு தடை. Super Admin ஐ தொடர்பு கொள்ள" : "Account blocked. Contact Super Admin");
      else if (status === 429) toast.error(lang === "ta" ? "அதிகமா முயற்சிச்சீங்க. கொஞ்சம் பிறகு முயற்சி செய்" : "Too many attempts. Try again later.");
      else toast.error(lang === "ta" ? "தவறான PIN" : "Invalid PIN");
      setPin("");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (pin.length === 4) submit(); /* eslint-disable-next-line */ }, [pin]);

  // Force-black input text style — bullet-proof against any parent text-white cascade.
  const inputStyle = {
    color: "#0A1A3F",
    WebkitTextFillColor: "#0A1A3F",
    caretColor: "#0A1A3F",
  };

  // Language picker — branding-first splash
  if (showLangPicker) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-[#0A1A3F]">
        <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-[#0055FF]/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-[#FF3B30]/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 min-h-screen grid place-items-center p-6">
          <div className="bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-[2rem] p-10 max-w-md w-full shadow-2xl fade-up">
            <div className="flex items-center justify-center mb-6">
              <GtLogo size="w-56 h-56 sm:w-64 sm:h-64" />
            </div>
            <div className="text-center mb-8">
              <h1 className="heading-font text-4xl font-bold text-white tracking-tight">GT Royal</h1>
              <div className="text-sm text-white/70 uppercase tracking-[0.3em] mt-1.5 font-semibold">Beedi &amp; Matches</div>
            </div>
            <p className="text-center text-white/70 mb-6 text-sm flex items-center justify-center gap-2">
              <Languages className="w-4 h-4" /> {t("choose_language")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="lang-en"
                onClick={() => { setLang("en"); localStorage.setItem("xtra_lang_picked", "1"); setShowLangPicker(false); }}
                className="bg-white hover:bg-white/95 text-[#0A1A3F] rounded-2xl py-5 font-bold transition flex flex-col items-center gap-1 shadow-lg active:scale-[0.97]"
              >
                <span className="heading-font text-2xl">EN</span>
                <span className="text-sm font-semibold">English</span>
              </button>
              <button
                data-testid="lang-ta"
                onClick={() => { setLang("ta"); localStorage.setItem("xtra_lang_picked", "1"); setShowLangPicker(false); }}
                className="bg-white hover:bg-white/95 text-[#0A1A3F] rounded-2xl py-5 font-bold transition flex flex-col items-center gap-1 shadow-lg active:scale-[0.97]"
              >
                <span className="heading-font text-2xl">த</span>
                <span className="text-sm font-semibold">தமிழ்</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // NumPad inline component for PIN entry
  const NumPad = ({ onPress, onBack }) => {
    const keys = ["1","2","3","4","5","6","7","8","9","","0","del"];
    return (
      <div className="grid grid-cols-3 gap-2.5 max-w-xs mx-auto">
        {keys.map((k, i) => {
          if (k === "") return <div key={i} />;
          if (k === "del") return (
            <button key={i} onClick={onBack} className="rounded-2xl bg-white hover:bg-gray-50 border border-gray-200 py-4 transition active:scale-95 shadow-sm" data-testid="numpad-del">
              <Delete className="w-5 h-5 mx-auto text-[#86868B]" />
            </button>
          );
          return (
            <button key={i} onClick={() => onPress(k)} className="rounded-2xl bg-white hover:bg-gray-50 border border-gray-200 py-4 text-xl font-bold heading-font text-[#1D1D1F] transition active:scale-95 shadow-sm" data-testid={`numpad-${k}`}>
              {k}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0A1A3F]">
      <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#0055FF]/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-[#FF3B30]/15 blur-3xl pointer-events-none" />

      <div className="relative z-10 min-h-screen flex items-center justify-center p-5 sm:p-8">
        <div className="w-full max-w-md">
          {/* Brand block — large logo, no marketing copy */}
          <div className="flex flex-col items-center text-center mb-6">
            <GtLogo size="w-44 h-44 sm:w-52 sm:h-52" className="mb-4" />
            <h1 className="heading-font text-4xl sm:text-5xl font-bold text-white tracking-tight">GT Royal</h1>
            <div className="text-xs sm:text-sm text-white/70 uppercase tracking-[0.3em] mt-2 font-semibold">
              Beedi &amp; Matches
            </div>
          </div>

          {/* Lang toggle (mobile-style, but always shown) */}
          <div className="flex items-center justify-center mb-4">
            <button
              data-testid="login-lang-toggle"
              onClick={() => setLang(lang === "en" ? "ta" : "en")}
              className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-semibold text-white flex items-center gap-1.5 transition"
            >
              <Languages className="w-3.5 h-3.5" />
              {lang === "en" ? "தமிழ்" : "English"}
            </button>
          </div>

          <div className="bg-white rounded-[2rem] shadow-2xl p-7 sm:p-9 fade-up">
            {step === "phone" && (
              <div className="space-y-5">
                <div>
                  <h2 className="heading-font text-2xl sm:text-3xl font-bold text-[#1D1D1F] tracking-tight">{t("welcome")}</h2>
                  <p className="text-[#86868B] mt-1.5 text-sm">{t("enter_phone")}</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-[#86868B] font-bold mb-2 block">{t("phone")}</label>
                  <div className="relative">
                    <Phone className="w-5 h-5 text-[#86868B] absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="phone-input"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setLookupErr(null); }}
                      placeholder="9999999999"
                      autoFocus
                      style={inputStyle}
                      className="w-full bg-[#F5F5F7] border border-transparent rounded-2xl pl-12 pr-4 py-4 text-lg font-semibold placeholder:text-[#9CA3AF] focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF] outline-none transition"
                    />
                  </div>
                  {lookupErr && <div className="mt-2 text-sm text-[#FF3B30] font-semibold flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> {lookupErr}</div>}
                </div>
                <button
                  data-testid="phone-next"
                  disabled={phone.length !== 10 || busy}
                  onClick={lookupAndAdvance}
                  className="w-full bg-[#0A1A3F] hover:bg-[#0055FF] disabled:bg-gray-300 text-white rounded-2xl py-4 text-base font-bold transition shadow-lg disabled:shadow-none active:scale-[0.98]"
                >
                  {busy ? t("loading") : `${t("next")} →`}
                </button>
              </div>
            )}

            {step === "pin" && (
              <div className="space-y-5 fade-up">
                <div className="text-center pb-1">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0055FF] to-[#0A1A3F] mb-3 shadow-lg">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-[10px] text-[#86868B] uppercase tracking-[0.2em] mb-1 font-semibold">{t("welcome_back")}</div>
                  <div className="heading-font text-2xl font-bold text-[#1D1D1F] tracking-tight" data-testid="welcome-name">{name}</div>
                  <div className="text-sm text-[#86868B] flex items-center justify-center gap-1.5 mt-1">
                    <Phone className="w-3.5 h-3.5" /> {phone}
                  </div>
                  <p className="text-[11px] text-[#86868B] mt-2 leading-snug">
                    {lang === "ta" ? "உங்கள் 4-இலக்க PIN ஐ அழுத்துங்கள்" : "Enter your 4-digit PIN to continue"}
                  </p>
                </div>
                <div>
                  <div className="text-center text-xs text-[#86868B] mb-3 font-bold uppercase tracking-[0.18em]">{t("pin")}</div>
                  <div className="flex justify-center gap-2.5">
                    {[0,1,2,3].map(i => (
                      <div
                        key={i}
                        data-testid={`pin-dot-${i}`}
                        className={`w-12 h-12 rounded-2xl border-2 grid place-items-center text-xl font-bold transition-all ${pin.length > i ? "bg-[#0A1A3F] border-[#0A1A3F] text-white shadow-md" : "bg-white border-gray-200 text-transparent"}`}
                      >•</div>
                    ))}
                  </div>
                </div>
                <NumPad
                  onPress={(k) => !busy && pin.length < 4 && setPin(p => p + k)}
                  onBack={() => setPin(p => p.slice(0, -1))}
                />
                <button
                  data-testid="back-to-phone"
                  onClick={() => { setStep("phone"); setPin(""); setName(""); }}
                  className="w-full text-[#0055FF] hover:text-[#0044CC] py-2 font-semibold text-sm"
                >
                  ← {t("back")}
                </button>
              </div>
            )}
          </div>

          <div className="text-center text-[11px] text-white/50 mt-6">
            © GT Royal Beedi &amp; Matches
          </div>
        </div>
      </div>
    </div>
  );
}
